import * as Sentry from '@sentry/browser'
import * as _ from 'lodash'
import { pathOr } from 'ramda'
import { ValidationError } from 'yup'

export const transformErrors = (validationErrors: ValidationError[]) => {
  const errors = {}

  validationErrors.forEach(error => {
    if (error.path && !(error.path in errors)) {
      errors[error.path] = error.message
    }
  })

  return errors
}

type SentryErrorOptions = {
  extras?: any
  breadcrumb?: string
  name?: string
  message?: string
}

const mergeOptions = (opts: SentryErrorOptions) => {
  return {
    ...{
      breadcrumb: null,
      extras: {},
      name: null,
      message: null,
    },
    ...opts,
  }
}

const addBreadcrumb = (options: SentryErrorOptions) => {
  if (options.breadcrumb) {
    Sentry.addBreadcrumb({ message: options.breadcrumb })
  }
}

const isGQLError = (exception: any) => {
  return (
    exception &&
    // Disabling ESLint for this line for no reason other than the code existed like this before this ESLint rule went into effect.
    // If you're here from the future, please resolve this ESLint error!
    // eslint-disable-next-line no-prototype-builtins
    exception.hasOwnProperty('graphQLErrors') &&
    exception.graphQLErrors !== null &&
    exception.graphQLErrors !== undefined &&
    exception.graphQLErrors.length > 0
  )
}

const getGQLErrorName = error => {
  if (error.startsWith('PG::')) {
    return error.split(' ')[0].replace(/:/g, '')
  } else {
    return _.chain(error)
      .camelCase()
      .upperFirst()
      .value()
  }
}

const setTag = (scope, tag) => {
  if (tag) {
    scope.setTag('error_type', tag)
  }
}

const setExtras = (scope, extras) => {
  const size = JSON.stringify(extras).length
  if (size < 200 * 1024) {
    scope.setExtras(extras)
  }
}

export const sendToSentry = (exception: any, opts: SentryErrorOptions = {}) => {
  let tag = null
  const options = mergeOptions(opts)

  addBreadcrumb(options)

  if (isGQLError(exception)) {
    const error = pathOr(false, ['graphQLErrors', 0, 'data', 'errors', 0], exception)
    if (error) {
      exception.name = getGQLErrorName(error)
    }
    options.extras = { ...options.extras, graphQLErrors: exception.graphQLErrors }
    tag = 'gql_error'
  } else {
    if (options.name) {
      const tempName = exception.name
      exception.name = options.name
      options.name = tempName
    }
    if (options.message) {
      const tempMessage = exception.message
      exception.message = options.message
      options.message = tempMessage
    }
  }

  Sentry.withScope(scope => {
    setTag(scope, tag)
    setExtras(scope, options.extras)
    Sentry.captureException(exception)
  })
}

export class UnknownRoute extends Error {
  constructor(message) {
    super(message)
    this.name = 'UnknownRoute'
  }
}

export class FlowProviderError extends Error {
  constructor(message) {
    super(message)
    this.name = 'FlowProviderError'
  }
}

export class PlaidLinkExitError extends Error {
  constructor(message) {
    super(message)
    this.name = 'PlaidLinkExitError'
  }
}

export class ReactErrorBoundaryError extends Error {
  constructor(errorBoundaryError: Error, windowLocation: string) {
    super()

    this.name = 'ReactErrorBoundaryError'
    this.message = `Exception caught with error boundary at ${windowLocation}: ${errorBoundaryError}`
  }
}
